import { useEffect, useRef } from 'react'
import Message from './Message'
import TypingIndicator from './TypingIndicator'
import type { ChatMessage } from '../types'
import './MessageList.css'

export interface MessageListProps {
  messages: ChatMessage[]
  isLoading?: boolean
  onQuickActionClick?: (question: string) => void
}

export default function MessageList({
  messages,
  isLoading = false,
  onQuickActionClick,
}: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messageListRef = useRef<HTMLDivElement>(null)
  const isInitialLoadRef = useRef(true)

  useEffect(() => {
    if (messages.length === 0 && !isLoading) {
      isInitialLoadRef.current = true
      return
    }

    const scrollBehavior = isInitialLoadRef.current ? 'auto' : 'smooth'
    const scrollTimer = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: scrollBehavior })
        isInitialLoadRef.current = false
      }
    }, isInitialLoadRef.current ? 200 : 50)

    return () => clearTimeout(scrollTimer)
  }, [messages, isLoading])

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="message-list empty" ref={messageListRef}>
        <div className="empty-state">
          <p>Start a conversation by typing a message below.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="message-list" ref={messageListRef}>
      {messages.map((message) => (
        <Message
          key={message.id}
          message={message}
          onQuickActionClick={message.role === 'assistant' ? onQuickActionClick : undefined}
        />
      ))}
      {isLoading && <TypingIndicator />}
      <div ref={messagesEndRef} />
    </div>
  )
}
